import { axiosWithAuth } from '../api/interceprots'
interface GroupProduct {
	id: number
	name: string
	description: string
	benefits: string[]
	tags: string[]
	available: boolean
	products: Product[]
}

interface Product {
	available: boolean
	type: string // Определите конкретные типы, если это возможно
	created_at: string // Используйте Date, если необходимо
	updated_at: string // Используйте Date, если необходимо
	id: number
	name: string
	price: number
	description: string
	benefits: string[]
	tags: string[]
	duration_days: number
}

interface Store {
	id: number
	name: string
}

interface Item {
	status: number
	virtual_subscription: boolean
	auto_renew_status: number
	is_active: boolean
	is_in_billing_retry_period: boolean
	start_at: string // Используйте Date, если необходимо
	expires_at: string // Используйте Date, если необходимо
	id: string
	user_id: number
	group_product: GroupProduct
	store: Store
	subscription_price: number
}

export interface ApiResponse {
	count: number
	sum: number
	offset: number
	items: Item[]
}

/**
 * Доступ к странице конфигурации (ссылка-подписка). Разрешён только при наличии
 * активной подписки в «рабочем» статусе: 1 — Активна, 3 — Отменена (но действует
 * до конца оплаченного периода, в т.ч. при запланированной смене тарифа).
 * Пустой массив / отсутствие данных → доступа нет. Используется и сайдбаром
 * (показ пункта меню), и самой страницей (редирект в личный кабинет).
 */
export function hasConfigAccess(data?: ApiResponse): boolean {
	return (
		data?.items?.some(
			item => item.is_active && (item.status === 1 || item.status === 3)
		) ?? false
	)
}

export interface Discount {
	id: number
	discount_percentage: number
	months: number
}

/** Ответ upgrade-init — готовый конфиг виджета CloudPayments на доплату разницы. */
export interface UpgradeInitConfig {
	public_id: string
	amount: number
	currency: string
	description: string
	account_id: number
	data: {
		type: string
		subscription_id: string
		target_product_id: number
	}
}

/** Ответ POST /payments/link — готовая хостед-ссылка CloudPayments (§6.2 бэкенд-гайда). */
export interface PaymentLink {
	url: string
	order_id: string
	number: number
	amount: number
	currency: string
	product_id: number
}

export const PaymentsService = {
	/**
	 * Для Mini App/бота — НЕ то же самое, что чекаут на сайте (виджет
	 * CloudPayments в OrdersService+cart/page.tsx). Ссылку можно смело
	 * перегенерировать при каждом тапе "Оплатить": повторно оплатить один
	 * заказ CP не даст. Открывать строго через Telegram.WebApp.openLink —
	 * не внутри вебвью (3DS ломается, особенно на iOS, см. §6.4/§8).
	 */
	async createPaymentLink(product_id: number) {
		const response = await axiosWithAuth.post<PaymentLink>('payments/link', {
			product_id,
		})
		return response.data
	},
	async getPayments() {
		const response = await axiosWithAuth.get<ApiResponse>(
			'payments/subscriptions?count=5&offset=0'
		)
		return response.data
	},
	async cancelSubscription(Subscription_id: string) {
		const response = await axiosWithAuth.get(
			`cloudpayments/cancel/${Subscription_id}`
		)
		return response.data
	},
	async continueSubscription(Subscription_id: string) {
		const response = await axiosWithAuth.get(
			`cloudpayments/continue/${Subscription_id}`
		)
		return response.data
	},
	async getDiscounts() {
		const response = await axiosWithAuth.get('/discounts')
		return response.data
	},
	async applyDiscount(data: { discount_id: number }) {
		const response = await axiosWithAuth.post('/discounts/apply', data)
		return response.data
	},
	/** Понижение тарифа — запланированная смена со следующего периода, без оплаты. */
	async changeTariff(subscription_id: string, product_id: number) {
		const response = await axiosWithAuth.post(
			`payments/subscriptions/${subscription_id}/change-tariff`,
			{ product_id }
		)
		return response.data
	},
	/** Повышение тарифа — возвращает конфиг CloudPayments для доплаты разницы. */
	async upgradeInit(subscription_id: string, product_id: number) {
		const response = await axiosWithAuth.post<UpgradeInitConfig>(
			`payments/subscriptions/${subscription_id}/upgrade-init`,
			{ product_id }
		)
		return response.data
	},
	/** Отмена запланированной смены тарифа (передаётся id текущей активной подписки). */
	async cancelTariffChange(subscription_id: string) {
		// Тело {} обязательно: бэкенд отвергает пустой body при Content-Type json (400).
		const response = await axiosWithAuth.post(
			`payments/subscriptions/${subscription_id}/cancel-tariff-change`,
			{}
		)
		return response.data
	},
}
